import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/lib/toast';
import { X, Plus, Minus, RotateCcw, Trash2, History, Star, Crown } from 'lucide-react';
import {
 adminAdjustStampsService,
 getClientStampTransactions,
 subscribeToClientStampCards,
 subscribeToUserStamps,
} from '@/services/loyaltyService';

interface ClientStampModalProps {
 client: {
 uid: string;
 displayName?: string | null;
 email?: string | null;
 };
 franchiseId: string;
 onClose: () => void;
}

const ClientStampModal: React.FC<ClientStampModalProps> = ({ client, franchiseId, onClose }) => {
 const { t } = useTranslation();
 const [cards, setCards] = useState<any[]>([]);
 const [legacyActiveStamps, setLegacyActiveStamps] = useState(0);
 const [transactions, setTransactions] = useState<any[]>([]);
 const [activeTab, setActiveTab] = useState<'cards' | 'history'>('cards');
 const [loading, setLoading] = useState(true);
 const [processing, setProcessing] = useState(false);

 // Prompt state
 const [promptAction, setPromptAction] = useState<{
 action: 'add' | 'remove' | 'reset' | 'create_card' | 'delete_card';
 cardId?: string;
 maxStamps?: number;
 currentStamps?: number;
 } | null>(null);
 const [promptStamps, setPromptStamps] = useState(1);
 const [promptReason, setPromptReason] = useState('');

 // Premium card creation state
 const [isPremium, setIsPremium] = useState(false);
 const [premiumDiscountType, setPremiumDiscountType] = useState<'fixed' | 'percentage'>('percentage');
 const [premiumValue, setPremiumValue] = useState(50); // 50% or 50€ depending on type

 // Subscribe to stamp cards in real-time
 useEffect(() => {
 const unsubscribe = subscribeToClientStampCards(client.uid, franchiseId, (updatedCards) => {
 setCards(updatedCards);
 setLoading(false);
 });

 return () => unsubscribe();
 }, [client.uid, franchiseId]);

 // Subscribe to legacy loyalty_stamps in real-time
 useEffect(() => {
 const unsubscribe = subscribeToUserStamps(client.uid, franchiseId, (stamps) => {
 setLegacyActiveStamps(stamps.length);
 });

 return () => unsubscribe();
 }, [client.uid, franchiseId]);

 // Load transaction history when switching to history tab
 useEffect(() => {
 if (activeTab === 'history') {
 loadTransactions();
 }
 }, [activeTab]);

 const loadTransactions = async () => {
 try {
 const txs = await getClientStampTransactions(client.uid, franchiseId);
 setTransactions(txs);
 } catch (error) {
 console.error('Error loading transactions:', error);
 }
 };

 const handleAction = async () => {
 if (!promptAction) return;

 if (!promptReason.trim()) {
 toast.error(t('admin.clients.stamps.reasonRequired'));
 return;
 }

 setProcessing(true);
 try {
 const result = await adminAdjustStampsService({
 userId: client.uid,
 franchiseId,
 action: promptAction.action,
 cardId: promptAction.cardId,
 stamps: promptAction.action === 'add' || promptAction.action === 'remove' ? promptStamps : undefined,
 reason: promptReason.trim(),
 ...(promptAction.action === 'create_card' && isPremium && {
 premiumOptions: {
 isPremium: true,
 customDiscountType: premiumDiscountType,
 customDiscountValue: premiumDiscountType === 'fixed' ? premiumValue * 100 : premiumValue, // Convert euros to cents for fixed
 },
 }),
 });

 if (result.success) {
 const successKey = {
 add: 'admin.clients.stamps.stampAdded',
 remove: 'admin.clients.stamps.stampRemoved',
 reset: 'admin.clients.stamps.cardReset',
 create_card: 'admin.clients.stamps.cardCreated',
 delete_card: 'admin.clients.stamps.cardDeleted',
 }[promptAction.action];

 toast.success(t(successKey));
 setPromptAction(null);
 setPromptStamps(1);
 setPromptReason('');
 setIsPremium(false);
 setPremiumDiscountType('percentage');
 setPremiumValue(50);

 // Reload transactions if on history tab
 if (activeTab === 'history') {
 loadTransactions();
 }
 } else {
 toast.error(result.message || t('admin.clients.stamps.error'));
 }
 } catch (error: any) {
 console.error('Error adjusting stamps:', error);
 toast.error(error.message || t('admin.clients.stamps.error'));
 } finally {
 setProcessing(false);
 }
 };

 const openPrompt = (action: 'add' | 'remove' | 'reset' | 'create_card' | 'delete_card', cardId?: string, maxStamps?: number, currentStamps?: number) => {
 setPromptAction({ action, cardId, maxStamps, currentStamps });
 setPromptStamps(1);
 setPromptReason('');
 };

 const clientName = client.displayName || client.email || 'Cliente';

 // Combine stamps from both systems
 const totalCardStamps = cards.reduce((sum: number, c: any) => sum + (c.stamps || 0), 0);
 const totalRealStamps = totalCardStamps + legacyActiveStamps;
 const totalMaxStamps = cards.length > 0
 ? cards.reduce((sum: number, c: any) => sum + (c.maxStamps || 10), 0)
 : 10;

 // Check if there's an active card (to prevent creating duplicates)
 const hasActiveCard = cards.some((c: any) => c.status === 'active');

 const getTransactionTypeLabel = (type: string): string => {
 const key = `admin.clients.stamps.transactionTypes.${type}`;
 const translated = t(key);
 return translated === key ? type : translated;
 };

 const formatDate = (timestamp: any): string => {
 if (!timestamp) return 'N/A';
 const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
 return date.toLocaleDateString('es-ES', {
 day: '2-digit',
 month: '2-digit',
 year: 'numeric',
 hour: '2-digit',
 minute: '2-digit',
 });
 };

 return (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
 <div className="bg-surface rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
 {/* Header */}
 <div className="flex items-center justify-between p-4 border-b border-hairline">
 <h3 className="text-xl font-bold text-ink">
 {t('admin.clients.stamps.title', { name: clientName })}
 </h3>
 <button
 onClick={onClose}
 className="p-2 text-ink-subtle hover:text-ink-muted rounded-lg transition"
 >
 <X size={20} />
 </button>
 </div>

 {/* Tabs */}
 <div className="flex border-b border-hairline">
 <button
 onClick={() => setActiveTab('cards')}
 className={`flex-1 py-3 px-4 text-sm font-medium text-center transition ${
 activeTab === 'cards'
 ? 'text-info border-b-2 border-info/30'
 : 'text-ink-subtle hover:text-ink '
 }`}
 >
 <Star size={16} className="inline mr-2" />
 {t('admin.clients.stamps.cards')}
 </button>
 <button
 onClick={() => setActiveTab('history')}
 className={`flex-1 py-3 px-4 text-sm font-medium text-center transition ${
 activeTab === 'history'
 ? 'text-info border-b-2 border-info/30'
 : 'text-ink-subtle hover:text-ink '
 }`}
 >
 <History size={16} className="inline mr-2" />
 {t('admin.clients.stamps.history')}
 </button>
 </div>

 {/* Content */}
 <div className="flex-1 overflow-y-auto p-4">
 {loading ? (
 <div className="flex items-center justify-center py-12">
 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-info/30"></div>
 </div>
 ) : activeTab === 'cards' ? (
 <div className="space-y-4">
 {/* Total stamps summary - show when there are stamps from either system */}
 {(totalRealStamps > 0 || cards.length > 0) && (
 <div className="bg-surface-2 border border-accent/30 rounded-lg p-4 flex items-center justify-between">
 <div className="flex items-center gap-3">
 <div className="bg-accent/10 rounded-full p-2">
 <Star size={24} className="text-accent fill-accent" />
 </div>
 <div>
 <p className="text-sm text-accent font-medium">
 {t('admin.clients.stamps.totalStamps', 'Sellos actuales del cliente')}
 </p>
 <p className="text-xs text-accent">
 {cards.length > 0
 ? `${cards.length} ${cards.length === 1 ? t('admin.clients.stamps.card', 'tarjeta') : t('admin.clients.stamps.cardsCount', 'tarjetas')}`
 : t('admin.clients.stamps.noCards', 'Sin tarjetas')
 }
 {legacyActiveStamps > 0 && ` + ${legacyActiveStamps} sellos ganados`}
 </p>
 </div>
 </div>
 <div className="text-right">
 <span className="text-3xl font-bold text-accent">
 {totalRealStamps}
 </span>
 <span className="text-sm text-accent ml-1">
 / {totalMaxStamps}
 </span>
 </div>
 </div>
 )}

 {/* Legacy stamps (earned through service) - only show separately when no active card */}
 {legacyActiveStamps > 0 && !hasActiveCard && (
 <div className="border border-info/30 rounded-lg p-4 bg-info/10">
 <div className="flex items-center justify-between mb-3">
 <span className="px-2 py-1 rounded-full text-xs font-semibold bg-info/10 text-info bg-info/10 text-info">
 Sellos ganados por servicio
 </span>
 <span className="text-sm font-medium text-ink-muted">
 {legacyActiveStamps}
 </span>
 </div>
 <div className="flex gap-1 mb-2 flex-wrap">
 {Array.from({ length: legacyActiveStamps }).map((_, i) => (
 <Star
 key={i}
 size={20}
 className="text-info fill-info"
 />
 ))}
 </div>
 <p className="text-xs text-info">
 Estos sellos se ganaron automáticamente por servicios completados
 </p>
 </div>
 )}

 {/* Stamp cards */}
 {cards.length === 0 && legacyActiveStamps === 0 ? (
 <div className="text-center py-8 text-ink-subtle">
 <Star size={48} className="mx-auto mb-3 opacity-30" />
 <p>{t('admin.clients.stamps.noCards')}</p>
 </div>
 ) : (
 <>
 {cards.map((card) => {
 const cardStamps = card.stamps || 0;
 const maxStamps = card.maxStamps || 10;
 // For active card, show combined total (card + legacy stamps)
 const displayStamps = card.status === 'active'
 ? cardStamps + legacyActiveStamps
 : cardStamps;
 const totalRemovable = displayStamps;

 return (
 <div
 key={card.cardId}
 className={`border rounded-lg p-4 ${
 card.isPremium
 ? 'border-accent/30 bg-surface-2'
 : 'border-hairline'
 }`}
 >
 {/* Card header */}
 <div className="flex items-center justify-between mb-3">
 <div className="flex items-center gap-2">
 {card.isPremium && (
 <span className="px-2 py-1 rounded-full text-xs font-semibold bg-surface-2 text-ink flex items-center gap-1">
 <Crown size={12} />
 Premium
 </span>
 )}
 <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
 card.status === 'active'
 ? 'bg-success/10 text-success bg-success/10 text-success'
 : 'bg-accent/10 text-accent bg-accent/10 text-accent'
 }`}>
 {card.status === 'active'
 ? t('admin.clients.stamps.activeCard')
 : t('admin.clients.stamps.completedCard')}
 </span>
 {card.isPremium && card.customDiscountType && (
 <span className="px-2 py-1 rounded-full text-xs font-semibold bg-accent/10 text-accent bg-accent/10 text-accent">
 {card.customDiscountType === 'percentage'
 ? `${card.customDiscountValue}% dcto`
 : `${(card.customDiscountValue / 100).toFixed(0)}€ dcto`}
 </span>
 )}
 {card.status === 'active' && legacyActiveStamps > 0 && (
 <span className="px-2 py-1 rounded-full text-xs font-semibold bg-info/10 text-info bg-info/10 text-info">
 +{legacyActiveStamps} por servicio
 </span>
 )}
 </div>
 <span className="text-sm font-medium text-ink-muted">
 {displayStamps}/{maxStamps}
 </span>
 </div>

 {/* Stamps visual - show combined stamps for active card */}
 <div className="flex gap-1 mb-3 flex-wrap">
 {Array.from({ length: maxStamps }).map((_, i) => {
 const isLegacy = card.status === 'active' && i >= cardStamps && i < displayStamps;
 const isFilled = i < displayStamps;
 return (
 <Star
 key={i}
 size={24}
 className={isFilled
 ? isLegacy
 ? 'text-info fill-info'
 : 'text-accent fill-accent'
 : 'text-ink-muted'
 }
 />
 );
 })}
 </div>

 {/* Action buttons */}
 <div className="flex gap-2 flex-wrap">
 {card.status === 'active' && displayStamps < maxStamps && (
 <button
 onClick={() => openPrompt('add', card.cardId, maxStamps, displayStamps)}
 disabled={processing}
 className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-success text-white rounded-lg hover:bg-success/90 transition disabled:opacity-50"
 >
 <Plus size={14} />
 {t('admin.clients.stamps.addStamp')}
 </button>
 )}

 {totalRemovable > 0 && (
 <button
 onClick={() => openPrompt('remove', card.cardId, maxStamps, totalRemovable)}
 disabled={processing}
 className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-warning text-white rounded-lg hover:bg-warning/90 transition disabled:opacity-50"
 >
 <Minus size={14} />
 {t('admin.clients.stamps.removeStamp')}
 </button>
 )}

 {totalRemovable > 0 && (
 <button
 onClick={() => openPrompt('reset', card.cardId, maxStamps, totalRemovable)}
 disabled={processing}
 className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-surface-3 text-ink rounded-lg hover:bg-surface-3 transition disabled:opacity-50"
 >
 <RotateCcw size={14} />
 {t('admin.clients.stamps.resetCard')}
 </button>
 )}

 <button
 onClick={() => openPrompt('delete_card', card.cardId, maxStamps, displayStamps)}
 disabled={processing}
 className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-danger text-white rounded-lg hover:bg-danger/90 transition disabled:opacity-50"
 >
 <Trash2 size={14} />
 {t('admin.clients.stamps.deleteCard')}
 </button>
 </div>
 </div>
 );
 })}
 </>
 )}

 {/* Create new card button - disabled if there's already an active card */}
 {hasActiveCard ? (
 <div className="w-full text-center px-4 py-3 text-sm text-ink-subtle border-2 border-dashed border-hairline rounded-lg cursor-not-allowed">
 <p className="font-medium">No se puede crear otra tarjeta</p>
 <p className="text-xs mt-1">Ya existe una tarjeta activa. Complétala o elimínala primero.</p>
 </div>
 ) : (
 <button
 onClick={() => openPrompt('create_card')}
 disabled={processing}
 className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-info border-2 border-dashed border-info/30 rounded-lg hover:bg-info/20 transition disabled:opacity-50"
 >
 <Plus size={18} />
 {t('admin.clients.stamps.createCard')}
 </button>
 )}
 </div>
 ) : (
 /* History tab */
 <div className="overflow-x-auto">
 {transactions.length === 0 ? (
 <div className="text-center py-8 text-ink-subtle">
 <History size={48} className="mx-auto mb-3 opacity-30" />
 <p>{t('admin.clients.stamps.noCards')}</p>
 </div>
 ) : (
 <table className="w-full text-sm">
 <thead className="bg-surface-2">
 <tr>
 <th className="text-left py-2 px-3 font-medium text-ink-muted">
 {t('admin.clients.registration')}
 </th>
 <th className="text-left py-2 px-3 font-medium text-ink-muted">
 {t('common.type', 'Tipo')}
 </th>
 <th className="text-center py-2 px-3 font-medium text-ink-muted">
 {t('admin.clients.stamps.stampCount')}
 </th>
 <th className="text-left py-2 px-3 font-medium text-ink-muted">
 {t('admin.clients.stamps.cards')}
 </th>
 </tr>
 </thead>
 <tbody className="divide-y divide-hairline">
 {transactions.map((tx) => (
 <tr key={tx.transactionId} className="hover:bg-surface-2">
 <td className="py-2 px-3 text-ink-muted">
 {formatDate(tx.createdAt)}
 </td>
 <td className="py-2 px-3">
 <span className={`px-2 py-0.5 rounded text-xs font-medium ${
 tx.type.startsWith('admin_')
 ? 'bg-accent/10 text-accent bg-accent/10 text-accent'
 : tx.type === 'earned'
 ? 'bg-success/10 text-success bg-success/10 text-success'
 : 'bg-info/10 text-info bg-info/10 text-info'
 }`}>
 {getTransactionTypeLabel(tx.type)}
 </span>
 </td>
 <td className="py-2 px-3 text-center font-mono">
 <span className={tx.stampsAdded >= 0 ? 'text-success' : 'text-danger'}>
 {tx.stampsAdded >= 0 ? '+' : ''}{tx.stampsAdded}
 </span>
 </td>
 <td className="py-2 px-3 text-ink-subtle text-xs">
 {tx.stampsBefore} → {tx.stampsAfter}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 )}
 </div>
 )}
 </div>

 {/* Action prompt overlay */}
 {promptAction && (
 <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center z-10 rounded-lg">
 <div className="bg-surface rounded-lg p-6 m-4 max-w-sm w-full shadow-xl">
 <h4 className="text-lg font-bold text-ink mb-4">
 {promptAction.action === 'add' && t('admin.clients.stamps.addStamp')}
 {promptAction.action === 'remove' && t('admin.clients.stamps.removeStamp')}
 {promptAction.action === 'reset' && t('admin.clients.stamps.resetCard')}
 {promptAction.action === 'create_card' && t('admin.clients.stamps.createCard')}
 {promptAction.action === 'delete_card' && t('admin.clients.stamps.deleteCard')}
 </h4>

 {/* Stamp count selector for add/remove */}
 {(promptAction.action === 'add' || promptAction.action === 'remove') && (() => {
 const maxVal = promptAction.action === 'remove'
 ? promptAction.currentStamps || 10
 : Math.min(10, (promptAction.maxStamps || 10) - (promptAction.currentStamps || 0));
 return (
 <div className="mb-4">
 <label className="block text-sm font-medium text-ink-muted mb-2">
 {t('admin.clients.stamps.stampCount')}
 </label>
 <div className="flex items-center justify-center gap-4">
 <button
 type="button"
 onClick={() => setPromptStamps(Math.max(1, promptStamps - 1))}
 disabled={promptStamps <= 1}
 className="w-10 h-10 flex items-center justify-center rounded-full bg-surface-2 text-ink-muted font-bold text-lg hover:bg-surface-3 disabled:opacity-30 transition"
 >
 -
 </button>
 <span className="text-3xl font-bold text-ink min-w-[3rem] text-center">
 {promptStamps}
 </span>
 <button
 type="button"
 onClick={() => setPromptStamps(Math.min(maxVal, promptStamps + 1))}
 disabled={promptStamps >= maxVal}
 className="w-10 h-10 flex items-center justify-center rounded-full bg-surface-2 text-ink-muted font-bold text-lg hover:bg-surface-3 disabled:opacity-30 transition"
 >
 +
 </button>
 </div>
 <p className="text-xs text-center text-ink-subtle mt-1">
 {promptAction.action === 'remove' ? 'Disponibles' : 'Espacios libres'}: {maxVal}
 </p>
 </div>
 );
 })()}

 {/* Premium card options for create_card */}
 {promptAction.action === 'create_card' && (
 <div className="mb-4 space-y-3">
 {/* Premium toggle */}
 <label className="flex items-center gap-3 cursor-pointer">
 <input
 type="checkbox"
 checked={isPremium}
 onChange={(e) => setIsPremium(e.target.checked)}
 className="w-5 h-5 text-accent border-hairline rounded focus:ring-accent"
 />
 <div className="flex items-center gap-2">
 <Crown size={16} className="text-accent" />
 <span className="text-sm font-semibold text-ink">
 Tarjeta Premium (descuento personalizado)
 </span>
 </div>
 </label>

 {isPremium && (
 <div className="bg-accent/10 border border-accent/30 rounded-lg p-4 space-y-3">
 {/* Discount type selector */}
 <div>
 <label className="block text-xs font-medium text-ink-muted mb-2">
 Tipo de descuento
 </label>
 <div className="flex gap-2">
 <button
 type="button"
 onClick={() => { setPremiumDiscountType('percentage'); setPremiumValue(50); }}
 className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition ${
 premiumDiscountType === 'percentage'
 ? 'bg-accent text-on-accent'
 : 'bg-surface text-ink-muted border border-hairline'
 }`}
 >
 Porcentaje (%)
 </button>
 <button
 type="button"
 onClick={() => { setPremiumDiscountType('fixed'); setPremiumValue(10); }}
 className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition ${
 premiumDiscountType === 'fixed'
 ? 'bg-accent text-on-accent'
 : 'bg-surface text-ink-muted border border-hairline'
 }`}
 >
 Valor fijo (€)
 </button>
 </div>
 </div>

 {/* Value input */}
 <div>
 <label className="block text-xs font-medium text-ink-muted mb-1">
 {premiumDiscountType === 'percentage' ? 'Porcentaje de descuento' : 'Valor del descuento'}
 </label>
 <div className="flex items-center gap-2">
 <input
 type="number"
 value={premiumValue}
 onChange={(e) => setPremiumValue(Math.max(1, Number(e.target.value)))}
 min={1}
 max={premiumDiscountType === 'percentage' ? 100 : 999}
 className="w-24 px-3 py-2 border border-hairline rounded-lg bg-surface text-ink text-center font-bold text-lg"
 />
 <span className="text-lg font-bold text-ink-muted">
 {premiumDiscountType === 'percentage' ? '%' : '€'}
 </span>
 </div>
 </div>

 {/* Preview */}
 <div className="bg-surface rounded-lg p-3 text-center">
 <p className="text-xs text-ink-subtle mb-1">Al completar sellos, el cliente recibe:</p>
 <p className="text-lg font-bold text-accent">
 {premiumDiscountType === 'percentage'
 ? `${premiumValue}% de descuento`
 : `${premiumValue}€ de descuento`}
 </p>
 </div>
 </div>
 )}
 </div>
 )}

 {/* Confirm message for reset/delete */}
 {(promptAction.action === 'reset' || promptAction.action === 'delete_card') && (
 <p className="text-sm text-ink-muted mb-4">
 {promptAction.action === 'reset'
 ? t('admin.clients.stamps.confirmReset')
 : t('admin.clients.stamps.confirmDelete')}
 </p>
 )}

 {/* Reason input */}
 <div className="mb-4">
 <label className="block text-sm font-medium text-ink-muted mb-1">
 {t('admin.clients.stamps.reason')} *
 </label>
 <input
 type="text"
 value={promptReason}
 onChange={(e) => setPromptReason(e.target.value)}
 placeholder={t('admin.clients.stamps.reasonPlaceholder')}
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink"
 />
 </div>

 <div className="flex gap-3">
 <button
 onClick={handleAction}
 disabled={processing || !promptReason.trim()}
 className="flex-1 bg-info text-white px-4 py-2 rounded-lg hover:bg-info/90 disabled:opacity-50 font-medium"
 >
 {processing ? (
 <span className="flex items-center justify-center gap-2">
 <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
 </span>
 ) : (
 t('common.confirm', 'Confirmar')
 )}
 </button>
 <button
 onClick={() => setPromptAction(null)}
 disabled={processing}
 className="flex-1 bg-surface-2 text-ink-muted px-4 py-2 rounded-lg hover:bg-surface-3 font-medium"
 >
 {t('common.cancel')}
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 </div>
 );
};

export default ClientStampModal;
